import './mail-display-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AbstractEpticaController } from '@agent/agent-interface/contact-view/mail/abstract-mail-process';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import { HOME_PAGE_NAME, IEpticaRequestFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ITimelineEntry } from '@commons/history/timeline//models/timeline-entry';
import { ChannelUtil } from '@commons/util/helper/channel';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IChangesObject, IComponentController, IComponentOptions, ILogService, IPromise, IQService, isDefined } from 'angular';
import { i18n } from 'i18next';
import { map, tap } from 'rxjs';

import { AgentUserFacade, LoaderDeprecatedService, Option, ToasterFacade, truncate, TranslateService } from '@contact/upgrade';

import templateUrl from './mail-display-actions.template.html';

class MailDisplayActionsCtrl extends AbstractEpticaController<IEpticaRequestFrame> implements IComponentController {
  override responseId: string;
  currentInteractionId: string;
  inProcessActionsAllowed: boolean;
  historyPopinMode: string;
  assignFn: Function;
  closeFn: Function;
  reopenFn: Function;

  activeActions: boolean;
  hasVisibility: boolean;
  canAssign: boolean;
  mailSubject: string;
  disableReopen: boolean;
  displayReopen: boolean;
  agentHasAllSkills: boolean;
  private requestPromise: IPromise<IEpticaRequestFrame>;
  private lastEpticaRequestId: string = '';

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly $i18next: i18n,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly loaderDeprecatedService: LoaderDeprecatedService,
    private readonly toasterFacade: ToasterFacade,
    private readonly agentUserFacade: AgentUserFacade,
    override readonly router: AgentDesktopRouter,
    override readonly translateService: TranslateService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  /**
   * Close the mail (submit eptica's form)
   */
  close(): void {
    this.mainPromise().then(request =>
      this.loaderDeprecatedService
        .process(this.epticaDeprecatedService.safeCloseRequest(request.getId()))
        .pipe(
          tap({
            next: () =>
              this.toasterFacade.notification({
                message: this.$i18next.t('agent:toast.mail_close_success', { subject: truncate(this.mailSubject) }),
                duration: 5000,
              }),
            error: () =>
              this.toasterFacade.alert({
                message: this.$i18next.t('agent:toast.mail_close_failed', { subject: truncate(this.mailSubject) }),
              }),
          }),
          ng1Destroy(this)
        )
        .subscribe(() => this.closeFn())
    );
  }

  assign(): void {
    this.mainPromise().then(request =>
      this.loaderDeprecatedService
        .process(this.epticaDeprecatedService.safeAssignRequest(request.getId()))
        .pipe(
          tap({
            next: () =>
              this.toasterFacade.notification({
                message: this.$i18next.t('agent:toast.mail_assign_success', { subject: truncate(this.mailSubject) }),
                duration: 5000,
              }),
            error: () =>
              this.toasterFacade.alert({
                message: this.$i18next.t('agent:toast.mail_assign_failed', { subject: truncate(this.mailSubject) }),
              }),
          }),
          ng1Destroy(this)
        )
        .subscribe(() => {
          this.assignFn();
          this.canAssign = false;
        })
    );
  }

  /**
   * Reopen Mail on Eptica's side, then call the callBack
   */
  reopenMail(): void {
    this.disableReopen = true;
    if (!!this.mainPromise()) {
      this.mainPromise().then(request =>
        // reopen mail eptica side
        request.reOpen().then(
          epticaData => {
            if (epticaData.pageName === HOME_PAGE_NAME) {
              this.reopenFn();
            } else {
              this.$log.error('[ContactMailDisplayActions] After reopen, arrived to [' + epticaData.pageName + ']');
            }
          },
          err => {
            this.disableReopen = true;
            this.$log.error('[ContactMailDisplayActions] ' + JSON.stringify(err));
          }
        )
      );
    } else {
      this.disableReopen = false;
      this.$log.error(
        "[ContactMailDisplayActions] can't reOpen mail beacause the mail frame is null for interaction [" + this.currentInteractionId + ']'
      );
    }
  }

  checkEpticaDataAfterReload(_: IEpticaRequestFrame, epticaData: EpticaData): IPromise<EpticaData> {
    return this.$q.resolve(epticaData);
  }

  $onChanges(onChangesObj: { [property: string]: IChangesObject }): void {
    if (isDefined(onChangesObj['selectTimelineEntry'])) {
      Option.of(onChangesObj['selectTimelineEntry'].currentValue)
        .filter(timeline => isDefined(timeline.contactEntry))
        .filter(timeline => isDefined(timeline.contactEntry.epticaRequestId))
        .filter(() => this.lastEpticaRequestId !== onChangesObj['selectTimelineEntry'].currentValue.contactEntry.epticaRequestId)
        .sideEffect((timeline: ITimelineEntry) => {
          let newEpticaRequestId = onChangesObj['selectTimelineEntry'].currentValue.contactEntry.epticaRequestId;

          this.lastEpticaRequestId = newEpticaRequestId;
          this.activeActions = this.currentInteractionId !== timeline.id;
          this.hasVisibility = timeline.hasVisibility;
          this.canAssign = (timeline.responsibleId || 0).toString() !== this.epticaDeprecatedService.getMailAccountId();
          this.mailSubject = timeline.description;

          this.requestPromise = this.epticaDeprecatedService.loadRequest({
            requestId: parseInt(newEpticaRequestId, 10),
            responseId: this.responseId,
          });

          let selectSkillIds: any[] = timeline && timeline.contactEntry ? timeline.contactEntry.skills.map(skill => skill) : [];
          this.agentUserFacade.skills$
            .pipe(
              map(_csrSkills =>
                _csrSkills.filter(
                  csrSkill =>
                    ChannelUtil.equals(ChannelUtil.INCOMING_MAIL, csrSkill.channel) &&
                    selectSkillIds.find(mailSkill => csrSkill.skill.id === mailSkill.uuid)
                )
              ),
              ng1Destroy(this)
            )
            .subscribe(skills => {
              this.agentHasAllSkills = skills.length === selectSkillIds.length;
            });
        });
    }
  }

  mainPromise(): IPromise<IEpticaRequestFrame> {
    return this.requestPromise;
  }
}

export class MailDisplayActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
    selectTimelineEntry: '<',
    currentInteractionId: '<',
    inProcessActionsAllowed: '<',
    historyPopinMode: '<',
    disableReopen: '<',
    displayReopen: '<',
    closeFn: '&',
    assignFn: '&',
    reopenFn: '&',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$q',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    '$i18next',
    'toastDeprecatedService',
    'loaderDeprecatedService',
    'toasterFacade',
    'agentUserFacade',
    'agentDesktopRouter',
    'translateService',
    MailDisplayActionsCtrl,
  ];
}

export default 'contactMailDisplayActions';
